export type HazardType = 'fire' | 'flood' | 'storm' | 'wind' | 'cold' | 'heat' | 'weather' | 'earthquake' | 'transport' | 'rescue' | 'biosecurity' | 'other'
export type Severity = 'Extreme' | 'Severe' | 'Moderate' | 'Minor' | 'Unknown'
export type Urgency = 'Immediate' | 'Expected' | 'Future' | 'Past' | 'Unknown'
export type Certainty = 'Observed' | 'Likely' | 'Possible' | 'Unlikely' | 'Unknown'
export type MsgType = 'Alert' | 'Update' | 'Cancel' | 'Ack' | 'Error'

export interface GeoPoint {
  type: 'Point'
  coordinates: [number, number] // [lon, lat]
}

export interface AlertSummary {
  id: string
  cap_identifier: string
  sender: string
  sent_at: string
  msg_type: MsgType
  hazard_type: HazardType
  priority_score: number
  headline: string | null
  severity: Severity | null
  urgency: Urgency | null
  certainty: Certainty | null
  expires_at: string | null
  region_name: string | null
  centroid: GeoPoint | null
  geojson_url: string
}

export interface AlertInfo {
  id: string
  language: string
  categories: string[]
  event: string | null
  urgency: Urgency | null
  severity: Severity | null
  certainty: Certainty | null
  response_types: string[]
  headline: string | null
  description: string | null
  instruction: string | null
  web_url: string | null
  effective_at: string | null
  onset_at: string | null
  expires_at: string | null
}

export interface AlertArea {
  id: string
  description: string | null
  geocodes: { valueName: string; value: string }[] | null
  altitude_m: number | null
  ceiling_m: number | null
}

export interface AlertDetail extends AlertSummary {
  status: string
  scope: string
  references: string[] | null
  info_blocks: AlertInfo[]
  areas: AlertArea[]
}

export interface AlertsPage {
  items: AlertSummary[]
  total: number
  limit: number
  offset: number
}

// GeoJSON types for map rendering
export type GeoJSONGeometry = {
  type: string
  coordinates: unknown
}

export interface GeoJSONFeature<P = Record<string, unknown>> {
  type: 'Feature'
  id?: string | number
  geometry: GeoJSONGeometry
  properties: P
}

export interface GeoJSONFeatureCollection<P = Record<string, unknown>> {
  type: 'FeatureCollection'
  features: GeoJSONFeature<P>[]
}

// WebSocket message types
export type WsMessage =
  | { type: 'alert.new'; data: AlertSummary }
  | { type: 'alert.update'; data: AlertSummary }
  | { type: 'alert.cancel'; data: { id: string; references: string[] } }
  | { type: 'pong' }

export interface WsSubscribeFilter {
  hazard_types?: HazardType[]
  severity?: Severity[]
  min_priority?: number
  bbox?: [number, number, number, number]
}

// Filter state
export interface AlertFilters {
  hazard_types: HazardType[]
  severity: Severity[]
  active_only: boolean
  min_priority: number
  search: string
  region: string | null // AU state/territory code, e.g. "NSW" — null means no region filter
}

export const HAZARD_COLORS: Record<HazardType, string> = {
  fire:       '#F97316',
  flood:      '#3B8BD4',
  storm:      '#4F46E5',
  wind:       '#0EA5E9',
  cold:       '#06B6D4',
  heat:       '#DC2626',
  weather:    '#7F77DD',
  earthquake: '#BA7517',
  transport:  '#2A9D8F',
  rescue:     '#16A34A',
  biosecurity: '#78350F',
  other:      '#888780',
}

export const HAZARD_LABELS: Record<HazardType, string> = {
  fire:       'Fire',
  flood:      'Flood',
  storm:      'Storm',
  wind:       'Wind',
  cold:       'Cold',
  heat:       'Heat',
  weather:    'Weather',
  earthquake: 'Earthquake',
  transport:  'Transport',
  rescue:     'Rescue',
  biosecurity: 'Biosecurity',
  other:      'Other',
}

export const HAZARD_ICONS: Record<HazardType, string> = {
  fire:       '🔥',
  flood:      '🌊',
  storm:      '⚡',
  wind:       '💨',
  cold:       '❄️',
  heat:       '🌡️',
  weather:    '🌧️',
  earthquake: '🌏',
  transport:  '🚗',
  rescue:     '🚑',
  biosecurity: '🦠',
  other:      '⚠️',
}

export const ALL_HAZARD_TYPES: HazardType[] = [
  'fire', 'flood', 'storm', 'wind', 'cold', 'heat',
  'weather', 'earthquake', 'transport', 'rescue', 'biosecurity', 'other',
]
export const ALL_SEVERITIES: Severity[] = ['Extreme', 'Severe', 'Moderate', 'Minor', 'Unknown']

export const SEVERITY_COLORS: Record<Severity, string> = {
  Extreme:  '#DC2626',
  Severe:   '#F97316',
  Moderate: '#EAB308',
  Minor:    '#3B82F6',
  Unknown:  '#6B7280',
}
